"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Plus } from "lucide-react";
import type { Career } from "@/payload-types";
import { cn } from "@/lib/utils";
import { CSSStaggerContainer } from "@/components/animations/CSSStaggerContainer";
import { CSSStaggerItem } from "@/components/animations/CSSStaggerItem";
import { useCareersMarket } from "@/components/careers/CareersMarketContext";
import { marketOf, type Market } from "@/components/careers/market";

const CareerRow = ({ career }: { career: Career }) => {
  const { title, slug } = career;

  const locationTag = career.location?.isRemote
    ? "Remote"
    : career.location?.city;
  const tag = [locationTag, marketOf(career)].filter(Boolean).join(" · ");

  return (
    <CSSStaggerItem className="block border-b border-white/15">
      <Link
        href={`/careers/${slug}`}
        className="group flex items-center justify-between gap-6 w-full px-4 sm:px-6 lg:px-8 py-5"
      >
        <h3 className="text-lg md:text-xl font-subheading min-w-0 text-white transition-opacity group-hover:opacity-80">
          {title}
        </h3>
        <span className="ml-auto shrink-0 text-xs uppercase tracking-[0.2em] text-white/45 hidden sm:inline">
          {tag}
        </span>
        <Plus
          className="h-5 w-5 shrink-0 text-white/60 transition-transform group-hover:rotate-90 group-hover:text-white"
          aria-hidden="true"
        />
      </Link>
    </CSSStaggerItem>
  );
};

interface CareersGridClientProps {
  careers: Career[];
  // Set when a careers-hero on the page owns the UK/US filters.
  hideFilterBar?: boolean;
}

export function CareersGridClient({ careers, hideFilterBar }: CareersGridClientProps) {
  const ctx = useCareersMarket();
  const [localMarket, setLocalMarket] = useState<Market | null>(null);
  const activeMarket = ctx ? ctx.market : localMarket;
  const setActiveMarket = ctx ? ctx.setMarket : setLocalMarket;

  const availableMarkets = useMemo(() => {
    const present = new Set(careers.map(marketOf));
    return (["UK", "US"] as Market[]).filter((market) => present.has(market));
  }, [careers]);

  const filteredCareers = useMemo(
    () =>
      activeMarket
        ? careers.filter((career) => marketOf(career) === activeMarket)
        : careers,
    [careers, activeMarket]
  );

  const showFilter = availableMarkets.length > 1 && !hideFilterBar;

  return (
    <div className="w-full border-t border-white/15">
      {showFilter && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 sm:px-6 lg:px-8 py-5 border-b border-white/15">
          {availableMarkets.map((market) => {
            const isActive = activeMarket === market;
            return (
              <button
                key={market}
                type="button"
                aria-pressed={isActive}
                onClick={() =>
                  setActiveMarket((prev) => (prev === market ? null : market))
                }
                className={cn(
                  "group inline-flex items-center gap-2 text-sm transition-colors",
                  isActive ? "text-white" : "text-white/70 hover:text-white"
                )}
              >
                <span
                  className={cn(
                    "font-subheading text-lg leading-none transition-colors",
                    isActive ? "text-white" : "text-white/40 group-hover:text-white/70"
                  )}
                >
                  [
                </span>
                <span className="font-subheading">{market}</span>
                <Plus className="h-3.5 w-3.5" />
                <span
                  className={cn(
                    "font-subheading text-lg leading-none transition-colors",
                    isActive ? "text-white" : "text-white/40 group-hover:text-white/70"
                  )}
                >
                  ]
                </span>
              </button>
            );
          })}
        </div>
      )}
      <CSSStaggerContainer key={activeMarket ?? "all"} className="w-full">
        {filteredCareers.map((career) => (
          <CareerRow key={career.id} career={career} />
        ))}
      </CSSStaggerContainer>
    </div>
  );
}
